import { createApi } from "@reduxjs/toolkit/query/react";
import { QuizGroup } from "@src/pages/task-manager/quiz-group/quiz-group-table/types";
import { QuizDetails } from "@src/pages/task-manager/quiz-group/quizzesList/QuizType";
import { APIResponse } from "@src/types/CommonTypes";
import { baseQueryWithReauth } from "@src/utils/baseQuery";

export interface QuizGroupsDropdown {
  parentId: string;
  organizationType: string;
}

export interface GetQuizGroupsDropdownResponse extends APIResponse {
  response: { id: string; name: string }[];
}

export interface GetAllQuizGroupsResponse extends APIResponse {
  response: { content: QuizGroup[] };
}

export interface QuizGroupUrlResponse extends APIResponse {
  response: {
    matchedOrganizationIds: string[];
  };
}

export interface GetAllQuizzesOfQuizGroup extends APIResponse {
  response: { content: QuizDetails[]; totalElements: number };
}

export type QuizGroupActionsResponse = APIResponse;

export type AssignQuizGroupToAllResponse = APIResponse;

export type CreateQuizGroupResponse = APIResponse;

export const QuizGroupApiSlice = createApi({
  reducerPath: "QuizGroupApi",
  baseQuery: baseQueryWithReauth,
  endpoints: (builder) => ({
    getAllQuizGroups: builder.query<
      GetAllQuizGroupsResponse,
      {
        pageNo: number;
        pageSize: number;
        year: number;
        month: number;
        organizationId: string;
      }
    >({
      query: ({ pageNo, pageSize, year, month, organizationId }) => ({
        url: `quiz-group?pageNo=${pageNo}&pageSize=${pageSize}&year=${year}&month=${month}&organizationId=${organizationId}`,
        method: "GET",
      }),
    }),
    quizGroupActions: builder.mutation<
      QuizGroupActionsResponse,
      { groupId: string; action: string }
    >({
      query: ({ groupId, action }) => ({
        url: `quiz-group/update-group-status?groupId=${groupId}&action=${action}`,
        method: "PUT",
      }),
    }),
    assignQuizGroupToAll: builder.mutation<
      AssignQuizGroupToAllResponse,
      { groupId: string }
    >({
      query: ({ groupId }) => ({
        url: `quiz-group/assign-group-all?groupId=${groupId}`,
        method: "PUT",
      }),
    }),
    getQuizGroupsDropdown: builder.mutation<
      GetQuizGroupsDropdownResponse,
      { payload: QuizGroupsDropdown[]; quizId: string }
    >({
      query: ({ payload, quizId }) => ({
        url: `quiz-group/get-by-parent-multiple?quizId=${quizId}`,
        method: "POST",
        body: payload,
      }),
    }),
    createQuizGroup: builder.mutation<
      CreateQuizGroupResponse,
      { formData: FormData; organizationId: string }
    >({
      query: ({ formData, organizationId }) => ({
        url: `quiz-group?organizationId=${organizationId}`,
        method: "POST",
        body: formData,
      }),
    }),
    quizGroupUrl: builder.mutation<QuizGroupUrlResponse, { groupId: string }>({
      query: ({ groupId }) => ({
        url: `quiz-group/join-group-url?groupId=${groupId}`,
        method: "PUT",
      }),
    }),
    getAllQuizzesOfQuizGroup: builder.query<
      GetAllQuizzesOfQuizGroup,
      { quizGroupId: string; pageNo: number; pageSize: number }
    >({
      query: ({ quizGroupId, pageNo, pageSize }) => ({
        url: `quiz/get-quizGroup-quizzes?quizGroupId=${quizGroupId}&pageNo=${pageNo}&pageSize=${pageSize}`,
        method: "GET",
      }),
    }),
  }),
});

export const {
  useGetAllQuizGroupsQuery,
  useLazyGetAllQuizGroupsQuery,
  useQuizGroupActionsMutation,
  useAssignQuizGroupToAllMutation,
  useGetQuizGroupsDropdownMutation,
  useCreateQuizGroupMutation,
  useQuizGroupUrlMutation,
  useGetAllQuizzesOfQuizGroupQuery,
  useLazyGetAllQuizzesOfQuizGroupQuery,
} = QuizGroupApiSlice;
